/**
 * ISSUE #47 FIX 3.47.9: useCourseContent Custom Hook
 * Manages all non-exam course state and data fetching
 * Responsibilities: Chapter data, audio player state, progress tracking, EMI status
 */

import { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "../../../service/api";
import {
  getLessonProgress,
  getCourseProgress,
  updateLessonProgress,
  bulkUpdateProgress,
  createDebouncedProgressUpdater,
} from "../../../service/progressService";
import EMIService from "../../../service/emiService";
import { getTutorCourseContent } from "../../../service/courseApi";
import {
  getPlayerState,
  savePlayerState,
  requestCertificateEmail,
} from "../../../service/coursePlayerStateService";

// Helper to fix mojibake encoding issues
const decodePossibleMojibake = (str) => {
  if (typeof str !== "string") return str;
  if (!/[\u00C0-\u00FF]/.test(str)) return str;
  try {
    const bytes = new Uint8Array(Array.from(str).map((c) => c.charCodeAt(0)));
    const decoded = new TextDecoder("utf-8").decode(bytes);
    if (decoded && decoded !== str) return decoded;
    return str;
  } catch (e) {
    return str;
  }
};

/** Tutor content API uses lessonName, audioFiles, pdfFiles; player UI expects lessonname, audioFile, pdfFile. */
const normalizeTutorLessonFields = (lesson) => {
  if (!lesson || typeof lesson !== "object") return lesson;
  const audioFile = lesson.audioFile || lesson.audioFiles || [];
  const pdfFile = lesson.pdfFile || lesson.pdfFiles || [];
  const name =
    lesson.lessonname ||
    lesson.lessonName ||
    lesson.title ||
    "Lesson";
  return {
    ...lesson,
    _id: lesson._id || lesson.lessonId,
    lessonname: name,
    title: lesson.title || lesson.lessonName || lesson.lessonname,
    audioFile: Array.isArray(audioFile) ? audioFile : [],
    pdfFile: Array.isArray(pdfFile) ? pdfFile : [],
  };
};

const normalizeTutorChapterFields = (chapter) => {
  if (!chapter || typeof chapter !== "object") return chapter;
  return {
    ...chapter,
    _id: chapter._id || chapter.chapterId,
    lessons: (chapter.lessons || []).map(normalizeTutorLessonFields),
  };
};

export function useCourseContent() {
  const { id: courseId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const storageUserId =
    typeof window !== "undefined"
      ? localStorage.getItem("userId") || "guest"
      : "guest";

  const isTutorCourse = location.pathname.includes("/tutor-course/");
  const progressStorageKey = `courseProgress_${storageUserId}_${courseId}`;

  // --- CHAPTER & COURSE DATA STATE ---
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);

  // --- SELECTED LESSON STATE ---
  const [selectedLesson, setSelectedLesson] = useState(() => {
    try {
      const saved = localStorage.getItem(
        `coursePlayerState_${storageUserId}_${courseId}`
      );
      return saved ? JSON.parse(saved).selectedLesson : null;
    } catch {
      return null;
    }
  });

  // --- AUDIO PLAYER STATE ---
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(80);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [showPlaybackOptions, setShowPlaybackOptions] = useState(false);
  const [audioLoadError, setAudioLoadError] = useState(null);
  const [currentAudioFileIndex, setCurrentAudioFileIndex] = useState(() => {
    try {
      const saved = localStorage.getItem(
        `coursePlayerState_${storageUserId}_${courseId}`
      );
      return saved ? JSON.parse(saved).currentAudioFileIndex : 0;
    } catch {
      return 0;
    }
  });

  // --- AUDIO COMPLETION TRACKING ---
  const [completedAudioFiles, setCompletedAudioFiles] = useState(() => {
    try {
      const saved = localStorage.getItem(`audioCompletion_${courseId}`);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // --- PROGRESS TRACKING STATE ---
  const [userProgress, setUserProgress] = useState({
    completedLessons: [],
    attemptedExams: {},
  });

  const [purchaseDate, setPurchaseDate] = useState(() => {
    try {
      const saved = localStorage.getItem(`coursePurchaseDate_${courseId}`);
      return saved ? new Date(saved) : new Date();
    } catch {
      return new Date();
    }
  });

  // --- EMI & PAYMENT STATE ---
  const [isCourseLocked, setIsCourseLocked] = useState(false);
  const [emiPaymentRequired, setEmiPaymentRequired] = useState(false);
  const [overdueDetails, setOverdueDetails] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);

  // --- UI STATE ---
  const [activeTab, setActiveTab] = useState("playlist");
  const [selectedChapterIndex, setSelectedChapterIndex] = useState(
    selectedLesson?.chapterIndex || 0
  );
  const [showExamQuestions, setShowExamQuestions] = useState(false);
  const [contentRefreshKey, setContentRefreshKey] = useState(0);
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  // --- REFS ---
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const volumeBarRef = useRef(null);
  const playbackOptionsRef = useRef(null);
  const playOnLoadRef = useRef(false);
  const hasRestoredState = useRef(!!selectedLesson);

  const audioRetryRef = useRef({ count: 0, maxRetries: 3, currentUrl: null });
  const progressUpdaterRef = useRef(createDebouncedProgressUpdater());
  const pendingProgressUpdatesRef = useRef({});
  const certificateRequestedRef = useRef(false);

  // Refs to solve stale closure issues
  const selectedLessonRef = useRef(selectedLesson);
  const currentAudioFileIndexRef = useRef(currentAudioFileIndex);
  const chaptersRef = useRef(chapters);

  // Keep refs in sync with state changes
  useEffect(() => {
    selectedLessonRef.current = selectedLesson;
  }, [selectedLesson]);

  useEffect(() => {
    currentAudioFileIndexRef.current = currentAudioFileIndex;
  }, [currentAudioFileIndex]);

  useEffect(() => {
    chaptersRef.current = chapters;
  }, [chapters]);

  // --- HTML ELEMENT CLASS MANAGEMENT ---
  useEffect(() => {
    document.documentElement.classList.add("course-player-active");
    return () => {
      document.documentElement.classList.remove("course-player-active");
    };
  }, []);

  // --- AUDIO COMPLETION PERSISTENCE ---
  useEffect(() => {
    try {
      localStorage.setItem(
        `audioCompletion_${courseId}`,
        JSON.stringify(completedAudioFiles)
      );
    } catch (error) {
      console.error("Error saving audio completion state:", error);
    }
  }, [completedAudioFiles, courseId]);

  // --- ONLINE STATUS MONITORING ---
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // --- CONTENT LOADING ---
  // Single source of truth for whether the course is accessible: the actual
  // content endpoint (EMIService.getCourseContent / getTutorCourseContent).
  // It already enforces access server-side and returns the real
  // overdueCount/totalOverdue/nextDueAmount/nextDueDate on a 403
  // (Course-Controller.js/EMI-accessMiddleware.js), so there is no need for
  // a separate client-side pre-check to decide whether to attempt the fetch.
  useEffect(() => {
    const paymentJustCompleted =
      location.state?.paymentCompleted || location.state?.accessUnlocked;

    const fetchContent = async () => {
      setLoading(true);
      setError(null);
      setErrorCode(null);
      setIsCourseLocked(false);
      setEmiPaymentRequired(false);

      if (paymentJustCompleted) {
        setShowPaymentSuccess(true);
        setTimeout(() => setShowPaymentSuccess(false), 5000);
        sessionStorage.removeItem("emi_notified");
        sessionStorage.removeItem("payment_return_course");
        sessionStorage.removeItem("payment_type");
      }

      try {
        // Right after a payment, the webhook may not have flipped the EMI
        // installment to "paid" yet - retry a couple of times on a 403
        // instead of flashing "locked" for a payment that actually
        // succeeded. Not needed otherwise (single attempt).
        const maxAttempts = !isTutorCourse && paymentJustCompleted ? 3 : 1;
        let data = null;
        let lastError = null;

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          if (attempt > 1) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
          try {
            data = isTutorCourse
              ? await getTutorCourseContent(courseId)
              : await EMIService.getCourseContent(courseId);
            lastError = null;
            break;
          } catch (err) {
            lastError = err;
            if (err.response?.status === 403 && attempt < maxAttempts) {
              continue;
            }
            break;
          }
        }

        if (lastError) {
          throw lastError;
        }

        let fetchedChapters;

        if (data.success) {
          const responseData = data.data || data;

          if (isTutorCourse) {
            if (Array.isArray(responseData)) {
              fetchedChapters = responseData;
            } else if (Array.isArray(responseData.chapters)) {
              fetchedChapters = responseData.chapters;
            } else if (Array.isArray(responseData.lessons)) {
              fetchedChapters = responseData.lessons;
            } else if (Array.isArray(responseData.courseContent)) {
              fetchedChapters = responseData.courseContent;
            } else {
              const firstArrayValue = Object.values(responseData).find((v) =>
                Array.isArray(v)
              );
              fetchedChapters = firstArrayValue || [];
            }
          } else {
            fetchedChapters = Array.isArray(responseData)
              ? responseData
              : responseData?.data || [];
          }
        } else if (Array.isArray(data)) {
          fetchedChapters = data;
        } else if (data.data) {
          fetchedChapters = Array.isArray(data.data) ? data.data : [];
        } else {
          fetchedChapters = Array.isArray(data) ? data : [];
        }

        if (!Array.isArray(fetchedChapters)) {
          console.error("Invalid chapters format:", fetchedChapters);
          setError("Invalid course data format");
          setLoading(false);
          return;
        }

        // Decode any mojibake in chapter/lesson titles
        const decodedChapters = fetchedChapters.map((chapter) => ({
          ...chapter,
          title: decodePossibleMojibake(chapter.title),
          lessons: chapter.lessons?.map((lesson) => ({
            ...lesson,
            title: decodePossibleMojibake(
              lesson.title || lesson.lessonName || lesson.lessonname
            ),
            description: decodePossibleMojibake(lesson.description),
          })),
          exam: chapter.exam
            ? {
                ...chapter.exam,
                title: decodePossibleMojibake(chapter.exam.title),
              }
            : null,
        }));

        const chaptersForUi = isTutorCourse
          ? decodedChapters.map(normalizeTutorChapterFields)
          : decodedChapters;

        setChapters(chaptersForUi);

        // Server-verified purchase date drives drip-content lesson unlocking.
        // Falls back to the localStorage/today default when the API doesn't
        // provide one.
        if (data?.purchaseDate) {
          const serverPurchaseDate = new Date(data.purchaseDate);
          if (!Number.isNaN(serverPurchaseDate.getTime())) {
            setPurchaseDate(serverPurchaseDate);
            try {
              localStorage.setItem(
                `coursePurchaseDate_${courseId}`,
                serverPurchaseDate.toISOString()
              );
            } catch {
              // Ignore storage errors (e.g. private browsing quota)
            }
          }
        }

        // Fetch progress for non-tutor courses
        if (!isTutorCourse) {
          try {
            const progress = await getCourseProgress(courseId);
            console.log("Progress fetched:", progress);
            setUserProgress(progress || { completedLessons: [], attemptedExams: {} });
          } catch (progressError) {
            console.error("Error fetching progress:", progressError);
            setUserProgress({ completedLessons: [], attemptedExams: {} });
          }
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching course content:", error);

        if (error.response?.status === 404) {
          setError("Course not found");
          setErrorCode(404);
        } else if (error.response?.status === 403) {
          // Enrolled, but content is gated (overdue/locked EMI) - render the
          // "course locked, pay to continue" screen using the real amounts
          // the server always returns on this response now.
          const body = error.response?.data || {};
          setIsCourseLocked(true);
          setEmiPaymentRequired(true);
          setOverdueDetails({
            overdueCount: body.overdueCount || 0,
            totalOverdue: body.totalOverdue || 0,
            nextDueAmount: body.nextDueAmount || body.totalOverdue || 0,
            nextDueDate: body.nextDueDate,
          });
        } else {
          setError("Failed to load course content. Please try again.");
          setErrorCode(error.response?.status || 500);
        }

        setLoading(false);
      } finally {
        if (paymentJustCompleted) {
          navigate(location.pathname, { replace: true, state: {} });
        }
      }
    };

    fetchContent();
  }, [
    courseId,
    isTutorCourse,
    location.state?.paymentCompleted,
    location.state?.accessUnlocked,
    location.pathname,
    navigate,
  ]);

  // --- FETCH EXAM ATTEMPTS & MERGE PROGRESS ---
  useEffect(() => {
    if (!chapters.length || isTutorCourse) return;

    const fetchExamAttempts = async () => {
      try {
        const response = await api.get(`/api/v1/exam-questions/user/exam/result?courseId=${courseId}`);

        if (response.data?.success) {
          const examResults = response.data.data || [];

          // getPlayerState resolves the raw response body ({success, data}),
          // so the actual saved state is nested under .data
          const serverProgress = (await getPlayerState(courseId))?.data;

          setUserProgress((prev) => {
            const attemptedFromApi = {};
            examResults.forEach((result) => {
              let idx = result.chapterIndex;
              if (typeof idx !== "number" || idx < 0) {
                idx = chapters.findIndex(
                  (ch) =>
                    ch.exam &&
                    (String(ch.exam._id) === String(result.examId) ||
                      String(ch.exam.examId) === String(result.examId))
                );
              }
              if (typeof idx === "number" && idx >= 0) {
                attemptedFromApi[idx] = {
                  attempted: true,
                  result,
                };
              }
            });

            const mergedProgress = {
              completedLessons: [
                ...new Set([
                  ...(serverProgress?.completedLessons || []),
                  ...(prev.completedLessons || []),
                ]),
              ],
              attemptedExams: {
                ...(prev.attemptedExams || {}),
                ...attemptedFromApi,
              },
            };

            try {
              localStorage.setItem(
                progressStorageKey,
                JSON.stringify(mergedProgress)
              );
            } catch (e) {
              console.warn("Could not persist merged progress", e);
            }

            return mergedProgress;
          });

          // Merge server-synced audio-track completion (keeps this in step
          // with completedLessons instead of resetting per device)
          if (serverProgress?.completedAudioFiles?.length) {
            setCompletedAudioFiles((prev) => {
              const merged = { ...prev };
              serverProgress.completedAudioFiles.forEach((key) => {
                merged[key] = true;
              });
              return merged;
            });
          }
        }
      } catch (error) {
        console.error("Error fetching exam attempts:", error);
      }
    };

    fetchExamAttempts();
  }, [chapters, courseId, isTutorCourse, progressStorageKey]);

  // --- SAVE USER PROGRESS TO LOCALSTORAGE ---
  useEffect(() => {
    if (userProgress.completedLessons?.length > 0 || Object.keys(userProgress.attemptedExams || {}).length > 0) {
      try {
        localStorage.setItem(progressStorageKey, JSON.stringify(userProgress));
      } catch (error) {
        console.error("Error saving progress to localStorage:", error);
      }
    }
  }, [userProgress, progressStorageKey]);

  // --- DEBOUNCED SERVER PLAYER STATE SAVE ---
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const completedAudioKeys = Object.keys(completedAudioFiles).filter(
        (key) => completedAudioFiles[key]
      );

      if (
        userProgress.completedLessons?.length > 0 ||
        Object.keys(userProgress.attemptedExams || {}).length > 0 ||
        completedAudioKeys.length > 0
      ) {
        try {
          savePlayerState(courseId, {
            completedLessons: userProgress.completedLessons,
            attemptedExams: userProgress.attemptedExams,
            completedAudioFiles: completedAudioKeys,
          });
        } catch (error) {
          console.error("Error saving progress to server:", error);
        }
      }
    }, 1000);

    return () => clearTimeout(debounceTimer);
  }, [userProgress, completedAudioFiles, courseId]);

  // --- REQUEST CERTIFICATE WHEN ALL LESSONS COMPLETE ---
  useEffect(() => {
    if (
      !chapters.length ||
      isTutorCourse ||
      certificateRequestedRef.current ||
      !userProgress.completedLessons
    ) {
      return;
    }

    // Check if all lessons are completed
    let totalLessons = 0;
    chapters.forEach((chapter) => {
      totalLessons += chapter.lessons?.length || 0;
    });

    if (totalLessons > 0 && userProgress.completedLessons.length === totalLessons) {
      certificateRequestedRef.current = true;

      try {
        requestCertificateEmail(courseId)
          .then(() => {
            console.log("Certificate request sent");
          })
          .catch((error) => {
            console.error("Error requesting certificate:", error);
            certificateRequestedRef.current = false;
          });
      } catch (error) {
        console.error("Error requesting certificate:", error);
        certificateRequestedRef.current = false;
      }
    }
  }, [chapters, userProgress.completedLessons, courseId, isTutorCourse]);

  // --- HANDLE ONLINE/OFFLINE & FLUSH ON UNLOAD ---
  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(true);
      // Flush any pending updates when coming back online
      if (pendingProgressUpdatesRef.current && Object.keys(pendingProgressUpdatesRef.current).length > 0) {
        try {
          bulkUpdateProgress(pendingProgressUpdatesRef.current);
          pendingProgressUpdatesRef.current = {};
        } catch (error) {
          console.error("Error flushing pending updates:", error);
        }
      }
    };

    const handleOfflineStatus = () => {
      setIsOnline(false);
      console.warn("You are offline. Progress will be saved when connection is restored.");
    };

    const handleBeforeUnload = () => {
      // Flush pending updates before unload
      if (pendingProgressUpdatesRef.current && Object.keys(pendingProgressUpdatesRef.current).length > 0) {
        try {
          bulkUpdateProgress(pendingProgressUpdatesRef.current);
          pendingProgressUpdatesRef.current = {};
        } catch (error) {
          console.error("Error flushing pending updates on unload:", error);
        }
      }
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOfflineStatus);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOfflineStatus);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // --- AUDIO PLAYER HANDLERS ---
  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (newTime) => {
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume / 100;
    }
  };

  const changePlaybackRate = (newRate) => {
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
    setShowPlaybackOptions(false);
  };

  const handleSelectLesson = (lesson) => {
    setSelectedLesson(lesson);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setCurrentAudioFileIndex(0);

    try {
      savePlayerState(courseId, {
        selectedLesson: lesson,
        currentAudioFileIndex: 0,
      });
    } catch (error) {
      console.error("Error saving player state:", error);
    }
  };

  const skipNext = () => {
    if (
      selectedLesson?.audioFile &&
      currentAudioFileIndex < selectedLesson.audioFile.length - 1
    ) {
      setCurrentAudioFileIndex(currentAudioFileIndex + 1);
      setCurrentTime(0);
    }
  };

  const skipPrev = () => {
    if (currentAudioFileIndex > 0) {
      setCurrentAudioFileIndex(currentAudioFileIndex - 1);
      setCurrentTime(0);
    }
  };

  const handleAudioItemClick = (index) => {
    setCurrentAudioFileIndex(index);
    setCurrentTime(0);
    setIsPlaying(true);
  };

  const handleDownload = (audioUrl, fileName) => {
    const link = document.createElement("a");
    link.href = audioUrl;
    link.download = fileName || "audio.mp3";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleLessonComplete = async (lesson) => {
    try {
      const updated = await updateLessonProgress(courseId, lesson);
      console.log("Lesson marked as completed:", updated);

      setCompletedAudioFiles((prev) => ({
        ...prev,
        [lesson._id]: true,
      }));

      // Fetch updated progress
      const progress = await getCourseProgress(courseId);
      setUserProgress(progress || { completedLessons: [], attemptedExams: {} });
    } catch (error) {
      console.error("Error updating lesson progress:", error);
    }
  };

  return {
    // Chapter & Course Data
    chapters,
    loading,
    error,
    errorCode,
    setError,
    setErrorCode,

    // Selected Lesson
    selectedLesson,
    setSelectedLesson,

    // Audio Player State
    isPlaying,
    setIsPlaying,
    currentTime,
    setCurrentTime,
    duration,
    setDuration,
    volume,
    setVolume,
    playbackRate,
    setPlaybackRate,
    showPlaybackOptions,
    setShowPlaybackOptions,
    audioLoadError,
    setAudioLoadError,
    currentAudioFileIndex,
    setCurrentAudioFileIndex,

    // Audio Completion Tracking
    completedAudioFiles,
    setCompletedAudioFiles,

    // Progress Tracking
    userProgress,
    setUserProgress,
    purchaseDate,
    setPurchaseDate,

    // EMI & Payment
    isCourseLocked,
    setIsCourseLocked,
    emiPaymentRequired,
    setEmiPaymentRequired,
    overdueDetails,
    setOverdueDetails,
    showPaymentSuccess,
    setShowPaymentSuccess,

    // UI State
    activeTab,
    setActiveTab,
    selectedChapterIndex,
    setSelectedChapterIndex,
    showExamQuestions,
    setShowExamQuestions,
    contentRefreshKey,
    setContentRefreshKey,
    isOnline,
    setIsOnline,

    // Handlers
    handlePlayPause,
    handleSeek,
    handleVolumeChange,
    changePlaybackRate,
    handleSelectLesson,
    skipNext,
    skipPrev,
    handleAudioItemClick,
    handleDownload,
    handleLessonComplete,

    // Refs
    audioRef,
    progressBarRef,
    volumeBarRef,
    playbackOptionsRef,
    playOnLoadRef,
    hasRestoredState,
    audioRetryRef,
    progressUpdaterRef,
    pendingProgressUpdatesRef,
    certificateRequestedRef,
    selectedLessonRef,
    currentAudioFileIndexRef,
    chaptersRef,

    // Constants
    courseId,
    storageUserId,
    isTutorCourse,
    progressStorageKey,
  };
}
